/**
 * Manage Subscription page — Phase 2 Stage B.
 *
 * Lets paying users see their active subscription, upcoming charges, and
 * cancel (with end-of-period semantics — decision 2a). Also shows the
 * loyalty bonus indicator when applicable (decision 5a).
 *
 * Routes: /subscription/manage  (added to App.js routes table).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPost } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { ArrowLeft, CreditCard, CalendarClock, Gift, ShieldCheck, AlertTriangle, Loader2, XCircle } from 'lucide-react';
import { Hint, InfoDot } from '@/components/ui/hint';
import { HELP } from '@/lib/helpText';
import { useScreenTip } from '@/hooks/useScreenTip';

const STATUS_STYLES = {
  active:        { bg: 'bg-emerald-50',  text: 'text-emerald-700',  label: 'Active' },
  authenticated: { bg: 'bg-emerald-50',  text: 'text-emerald-700',  label: 'Active' },
  activated:     { bg: 'bg-emerald-50',  text: 'text-emerald-700',  label: 'Active' },
  created:       { bg: 'bg-slate-100',   text: 'text-slate-600',    label: 'Awaiting first charge' },
  pending:       { bg: 'bg-amber-50',    text: 'text-amber-700',    label: 'Mandate pending' },
  halted:        { bg: 'bg-rose-50',     text: 'text-rose-700',     label: 'Payment failed' },
  cancelled:     { bg: 'bg-slate-100',   text: 'text-slate-500',    label: 'Cancelled' },
  completed:     { bg: 'bg-slate-100',   text: 'text-slate-500',    label: 'Completed' },
};

export default function ManageSubscriptionPage() {
  const navigate = useNavigate();
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cancellingId, setCancellingId] = useState(null);
  useScreenTip('manage-subscription', HELP.tips.manageSubscription);
  // Iter 60y — reason-capture modal for the Cancel Subscription flow.
  // Every cancel must ship with a canonical reason so the churn chart
  // has structured data from day one.
  const [cancelModal, setCancelModal] = useState(null);  // { row } | null
  const [cancelReason, setCancelReason] = useState('');
  const [cancelFeedback, setCancelFeedback] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await apiGet('/subscriptions/mine');
      setSubs(r.subscriptions || []);
    } catch (e) {
      toast.error('Could not load subscriptions');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  // Cancel-at-period-end. Razorpay stops billing, user keeps access through
  // current cycle. Banner updates immediately; webhook flips status when
  // Razorpay confirms.
  // Iter 60y — Open the reason-picker modal instead of the browser confirm().
  const openCancelModal = (row) => {
    setCancelModal({ row });
    setCancelReason('');
    setCancelFeedback('');
  };

  const submitCancel = async () => {
    if (!cancelModal?.row) return;
    if (!cancelReason) {
      toast.error('Please pick a reason so we can help improve the app');
      return;
    }
    const row = cancelModal.row;
    setCancellingId(row.subscription_row_id);
    try {
      await apiPost(`/subscriptions/${row.subscription_row_id}/cancel`, {
        reason: cancelReason,
        feedback: cancelFeedback,
      });
      toast.success('Subscription cancelled — access continues until period end');
      setCancelModal(null);
      load();
    } catch (e) {
      toast.error(e?.message || 'Cancel failed');
    }
    setCancellingId(null);
  };

  const fmtDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch { return iso; }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-5" data-testid="manage-subscription-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" onClick={() => navigate(-1)} className="text-slate-500 hover:text-slate-900 rounded-full p-2 h-9 w-9" data-testid="manage-sub-back-btn">
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-heading font-semibold text-slate-900">Manage Subscription</h1>
          <p className="text-xs font-body text-slate-500">View and cancel your active plan</p>
        </div>
      </div>

      {loading && (
        <Card className="p-10 text-center" data-testid="manage-sub-loading">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400 mx-auto" />
          <p className="text-xs font-body text-slate-400 mt-3">Loading…</p>
        </Card>
      )}

      {!loading && subs.length === 0 && (
        <Card className="p-10 text-center" data-testid="manage-sub-empty">
          <CreditCard className="w-8 h-8 text-slate-300 mx-auto mb-3" />
          <p className="font-heading text-slate-700">No active subscriptions</p>
          <p className="text-xs font-body text-slate-500 mt-1 mb-4">You're on the free trial. Subscribe to Growth or go Elite Lifetime to unlock everything.</p>
          <Button onClick={() => navigate('/subscription')} className="bg-indigo-500 hover:bg-indigo-600 text-white rounded-full text-xs font-heading"
            data-testid="manage-sub-upgrade-cta">
            See plans
          </Button>
        </Card>
      )}

      {!loading && subs.map((row) => {
        const st = STATUS_STYLES[row.status] || { bg: 'bg-slate-100', text: 'text-slate-500', label: row.status };
        const isActive = ['active', 'authenticated', 'activated', 'created'].includes(row.status);
        const cancelling = cancellingId === row.subscription_row_id;
        return (
          <Card key={row.subscription_row_id}
            className="p-6 rounded-2xl border-slate-100"
            data-testid={`manage-sub-row-${row.subscription_row_id}`}>
            <div className="flex flex-wrap items-start gap-4">
              <div className="flex-1 min-w-[14rem]">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-heading text-base font-semibold text-slate-900">
                    {row.plan_key.split('_').map(w => w[0]?.toUpperCase() + w.slice(1)).join(' ')}
                  </h3>
                  <Badge className={`${st.bg} ${st.text} text-[10px] font-heading uppercase tracking-wider`}>
                    {st.label}
                  </Badge>
                  {row.is_upgrade && (
                    <Hint text={HELP.subscription.upgradeLoyalty}>
                      <Badge className="bg-amber-50 text-amber-700 text-[10px] font-heading uppercase tracking-wider">
                        <Gift className="w-3 h-3 mr-1 inline" /> Loyalty upgrade
                      </Badge>
                    </Hint>
                  )}
                </div>
                <p className="text-sm text-slate-600 font-body">
                  ₹{(row.amount_inr || 0).toLocaleString('en-IN')} <span className="text-xs text-slate-400">/{row.period}</span>
                </p>
                <div className="grid grid-cols-2 gap-y-1.5 gap-x-6 mt-4 text-xs font-body">
                  <div className="flex items-center gap-2 text-slate-500">
                    <CalendarClock className="w-3.5 h-3.5" /> Started
                  </div>
                  <div className="text-slate-700">{fmtDate(row.created_at)}</div>
                  {row.first_charge_at && (
                    <>
                      <div className="flex items-center gap-2 text-slate-500">
                        <Gift className="w-3.5 h-3.5" /> First charge
                        <InfoDot text={HELP.subscription.firstCharge} side="right" />
                      </div>
                      <div className="text-slate-700">{fmtDate(row.first_charge_at)}</div>
                    </>
                  )}
                  {row.cancelled_at && (
                    <>
                      <div className="flex items-center gap-2 text-rose-600">
                        <XCircle className="w-3.5 h-3.5" /> Cancelled
                      </div>
                      <div className="text-rose-700">{fmtDate(row.cancelled_at)}</div>
                    </>
                  )}
                  {row.halted_at && (
                    <>
                      <div className="flex items-center gap-2 text-rose-600">
                        <AlertTriangle className="w-3.5 h-3.5" /> Halted (payment failed)
                        <InfoDot text={HELP.subscription.halted} side="right" />
                      </div>
                      <div className="text-rose-700">
                        {fmtDate(row.halted_at)}
                        {row.grace_until && (
                          <span className="ml-2 text-[10px]">grace until {fmtDate(row.grace_until)}</span>
                        )}
                      </div>
                    </>
                  )}
                  {row.charges_captured > 0 && (
                    <>
                      <div className="flex items-center gap-2 text-slate-500">
                        <ShieldCheck className="w-3.5 h-3.5" /> Charges so far
                      </div>
                      <div className="text-slate-700">{row.charges_captured}</div>
                    </>
                  )}
                </div>
                {row.loyalty_bonus_days > 0 && (
                  <p className="text-xs text-amber-700 font-body mt-3 px-3 py-2 bg-amber-50 rounded-lg border border-amber-100">
                    <Gift className="w-3.5 h-3.5 inline mr-1" />
                    You got {row.loyalty_bonus_days} days free on this upgrade as a thank-you for being a paying customer.
                  </p>
                )}
                {row.cancel_at_period_end && !row.completed_at && (
                  <p className="text-xs text-slate-600 font-body mt-3 px-3 py-2 bg-slate-50 rounded-lg border border-slate-100">
                    No more charges will happen. Your access continues until the current billing period ends.
                  </p>
                )}
                {row.status === 'halted' && !row.halted_downgraded && (
                  <p className="text-xs text-rose-700 font-body mt-3 px-3 py-2 bg-rose-50 rounded-lg border border-rose-100">
                    Your last payment failed. We're keeping your access live for 3 days while you update your payment method on Razorpay.
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-2 shrink-0">
                {isActive && !row.cancel_at_period_end && (
                  <Hint text={HELP.subscription.cancel} side="left">
                    <Button onClick={() => openCancelModal(row)}
                      disabled={cancelling}
                      data-testid={`manage-sub-cancel-${row.subscription_row_id}`}
                      variant="ghost"
                      className="text-xs font-heading text-rose-600 hover:bg-rose-50 rounded-full disabled:opacity-40">
                      {cancelling ? 'Cancelling…' : 'Cancel'}
                    </Button>
                  </Hint>
                )}
                {(row.status === 'cancelled' || row.status === 'completed' || row.halted_downgraded) && (
                  <Hint text={HELP.subscription.resubscribe} side="left">
                    <Button onClick={() => navigate('/subscription')}
                      data-testid={`manage-sub-resub-${row.subscription_row_id}`}
                      className="bg-indigo-500 hover:bg-indigo-600 text-white rounded-full text-xs font-heading">
                      Resubscribe
                    </Button>
                  </Hint>
                )}
              </div>
            </div>
          </Card>
        );
      })}

      {/* Iter 60y — Cancel reason modal */}
      {cancelModal && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="cancel-modal"
          onClick={() => setCancelModal(null)}
        >
          <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-rose-50 flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-rose-500" />
              </div>
              <div>
                <h3 className="font-heading text-lg font-bold text-slate-900">Cancel subscription</h3>
                <p className="text-xs font-body text-slate-500 mt-0.5">
                  You&apos;ll keep access until the current billing period ends. No further charges will happen. Help us improve — why are you leaving?
                </p>
              </div>
            </div>
            <div className="space-y-2 mb-4">
              {[
                { k: 'too_expensive', l: 'Too Expensive' },
                { k: 'not_useful', l: 'Not Useful' },
                { k: 'technical_issues', l: 'Technical Issues' },
                { k: 'found_alternative', l: 'Found Alternative' },
                { k: 'temporary_break', l: 'Temporary Break' },
                { k: 'other', l: 'Other' },
              ].map(r => (
                <label
                  key={r.k}
                  data-testid={`cancel-reason-${r.k}`}
                  className={`flex items-center gap-3 px-3 py-2 rounded-xl border cursor-pointer transition-all ${
                    cancelReason === r.k
                      ? 'border-rose-400 bg-rose-50'
                      : 'border-slate-200 hover:border-slate-300 bg-white'
                  }`}
                >
                  <input
                    type="radio"
                    name="cancel-reason"
                    value={r.k}
                    checked={cancelReason === r.k}
                    onChange={() => setCancelReason(r.k)}
                    className="accent-rose-500"
                  />
                  <span className="text-sm font-body text-slate-700">{r.l}</span>
                </label>
              ))}
            </div>
            <textarea
              data-testid="cancel-feedback"
              value={cancelFeedback}
              onChange={e => setCancelFeedback(e.target.value.slice(0, 500))}
              placeholder="Tell us more (optional, max 500 chars)"
              rows={3}
              className="w-full px-3 py-2 text-sm font-body rounded-xl border border-slate-200 focus:border-rose-400 focus:outline-none resize-none mb-4"
            />
            <div className="flex items-center justify-end gap-2">
              <button
                data-testid="cancel-modal-close"
                onClick={() => setCancelModal(null)}
                className="px-4 py-2 rounded-full text-sm font-heading text-slate-600 hover:bg-slate-100 transition">
                Keep my plan
              </button>
              <Button
                data-testid="cancel-modal-submit"
                onClick={submitCancel}
                disabled={!cancelReason || cancellingId === cancelModal.row?.subscription_row_id}
                className="bg-rose-500 hover:bg-rose-600 text-white rounded-full text-sm font-heading">
                {cancellingId === cancelModal.row?.subscription_row_id ? 'Cancelling…' : 'Confirm cancel'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
